import { useEffect, useRef, useState } from 'react';
import { useAuthSessionRevision } from 'src/auth/sessionEvents';
import { readWorkspaceSessionIdentity, workspaceSessionStamp } from 'src/auth/workspaceSession';
import { createQuoteEditorVersion, getQuoteEditorDraft, getQuoteEditorVersion, saveQuoteEditorDraft } from 'src/helper/backend_helper';

export const editableAccountQuoteDraft = (snapshot: any) => {
  const inputs = { ...snapshot };
  delete inputs.adminFeeValue; delete inputs.discountValue;
  return inputs;
};
export const accountQuoteDraftContent = (snapshot: any) => JSON.stringify(editableAccountQuoteDraft(snapshot), (key, value) => {
  if (key === '_base') return undefined;
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.keys(value).sort().map(field => [field, value[field]])) : value;
});
const content = accountQuoteDraftContent;
const commandId = () => `editor:${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
const message = (error: any) => error?.response?.data?.message ||
  (error?.message && !['Network Error', 'Failed to fetch', 'offline'].includes(error.message) ? error.message :
    'Your account draft could not be saved. Check your connection, keep this quote open, and choose Retry Saving.');
const sessionMessage = 'Your sign-in changed. Return to the original account to continue saving this quote.';

/** One account/event queue owns the optimistic revision; old responses cannot acknowledge newer input. */
const quoteDraftTransport = { getQuoteEditorDraft, getQuoteEditorVersion, createQuoteEditorVersion, saveQuoteEditorDraft };
export function useAccountQuoteDraft({ id, enabled, signature, capture, apply, editVersion,
  transport = quoteDraftTransport, scopeKey = 'quote', preserveCurrentOnRestore = false }: {
  transport?: typeof quoteDraftTransport; scopeKey?: string; preserveCurrentOnRestore?: boolean;
  id: number | string; enabled: boolean; signature: string; capture: () => any;
  apply: (snapshot: any, canonical: any, requestKey: string) => any; editVersion: () => number;
}) {
  const { getQuoteEditorDraft, getQuoteEditorVersion, createQuoteEditorVersion, saveQuoteEditorDraft } = transport;
  useAuthSessionRevision();
  const owner = useRef(readWorkspaceSessionIdentity());
  const stamp = workspaceSessionStamp();
  const identity = `${scopeKey}:${id}:${stamp}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const callbacks = useRef({ capture, apply, editVersion });
  callbacks.current = { capture, apply, editVersion };
  const [state, setState] = useState<any>({ status: 'loading', versions: [], error: '', selected: '' });
  const stateRef = useRef<any>(null);
  const acknowledged = useRef('');
  const namedContent = useRef('');
  const initialSnapshot = useRef<any>(null);
  const sequence = useRef<Promise<any>>(Promise.resolve());
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const epoch = useRef(0);
  const mounted = useRef(true);
  const retryCommand = useRef<any>(null);
  const paused = useRef(false);
  const suspendedOwner = useRef<any>(null);
  const owns = (key = identity) => mounted.current && enabled && identityRef.current === key &&
    workspaceSessionStamp() === stamp && readWorkspaceSessionIdentity() === owner.current;
  const cancelTimer = () => { if (timer.current) clearTimeout(timer.current); };
  const update = (values: any) => { if (mounted.current) setState((previous: any) => ({ ...previous, ...values })); };

  useEffect(() => {
    if (!enabled) return;
    mounted.current = true;
    const generation = ++epoch.current;
    if (suspendedOwner.current?.id !== String(id)) suspendedOwner.current = null;
    if (!owns() && !suspendedOwner.current) {
      suspendedOwner.current = { id: String(id), base: stateRef.current?.working?.snapshot ?? initialSnapshot.current };
    }
    const returningOwner = owns() ? suspendedOwner.current : null;
    stateRef.current = null;
    retryCommand.current = null;
    acknowledged.current = '';
    initialSnapshot.current = callbacks.current.capture();
    if (!owns()) {
      update({ status: 'unavailable', versions: [], error: sessionMessage, selected: '' });
      return () => { mounted.current = false; ++epoch.current; cancelTimer(); };
    }
    const inputVersion = callbacks.current.editVersion();
    update({ status: 'loading', versions: [], error: '', selected: '' });
    void getQuoteEditorDraft(id).then(response => {
      if (!owns() || generation !== epoch.current) return;
      const result = response?.data?.data;
      if (!result?.baseRevision) throw new Error('Account draft storage is unavailable. Keep this quote open and retry.');
      stateRef.current = result;
      const edited = Boolean(returningOwner) || callbacks.current.editVersion() !== inputVersion;
      if (edited) {
        callbacks.current.apply({ ...callbacks.current.capture(), _base: returningOwner?.base ?? initialSnapshot.current }, result.working?.snapshot ?? result.canonical, result.requestKey);
        suspendedOwner.current = null;
      } else if (result.working) {
        const applied = callbacks.current.apply(result.working.snapshot, result.canonical, result.requestKey);
        acknowledged.current = content(applied);
      } else if (!edited && result.initial) {
        acknowledged.current = content(callbacks.current.apply(result.initial, result.canonical, result.requestKey));
      } else if (!edited) acknowledged.current = content(callbacks.current.capture());
      update({ status: edited ? 'pending' : 'saved', versions: result.versions, error: '', updatedAt: result.working?.updatedAt });
    }).catch(error => {
      if (owns() && generation === epoch.current) update({ status: 'unavailable', error: message(error) });
    });
    return () => { mounted.current = false; ++epoch.current; cancelTimer(); };
    // The identity includes the quote and stable account generation, never credentials.
  }, [identity, enabled]);

  const write = (snapshot: any, version: boolean, generation = epoch.current) => {
    const operation = async () => {
      if (!owns() || paused.current || generation !== epoch.current) return null;
      const current = stateRef.current;
      if (!current) throw new Error('Account draft storage is unavailable. Keep this quote open and retry.');
      update({ status: 'saving', error: '' });
      const snapshotSignature = content(snapshot);
      const retry = retryCommand.current;
      const input = retry?.signature === snapshotSignature && retry?.version === version ? retry.input : {
        revision: current.working?.revision ?? 0, baseRevision: current.baseRevision, requestKey: current.requestKey,
        snapshot: { ...snapshot, worksheetMembership: snapshot.worksheetMembership ? { ...snapshot.worksheetMembership, requestKey: current.requestKey } : undefined },
        commandId: commandId()
      };
      retryCommand.current = { signature: snapshotSignature, version, input };
      const response = await (version ? createQuoteEditorVersion(id, input) : saveQuoteEditorDraft(id, input));
      if (!owns()) return null;
      const result = response?.data?.data;
      if (!result?.working?.revision) throw new Error('The server did not confirm this account save. Keep your edits open and retry.');
      // A same-quote restore drains this write and needs its revision. Other identities never do.
      stateRef.current = result;
      retryCommand.current = null;
      if (generation === epoch.current) {
        acknowledged.current = snapshotSignature;
        if (version) namedContent.current = snapshotSignature;
        update({ status: content(callbacks.current.capture()) === snapshotSignature ? 'saved' : 'pending',
          versions: result.versions, updatedAt: result.working.updatedAt,
          ...(version ? { selected: result.savedVersion?.key ?? '' } : {}) });
      }
      return response;
    };
    const queued = sequence.current.catch(() => null).then(operation);
    sequence.current = queued;
    return queued.catch(error => {
      if (owns() && generation === epoch.current) update({ status: error?.response?.status === 409 ? 'conflict' : 'error', error: message(error) });
      return null;
    });
  };

  useEffect(() => {
    cancelTimer();
    if (!owns() || !stateRef.current || ['loading', 'unavailable', 'error', 'conflict', 'restoring', 'paused'].includes(state.status)) return;
    if (content(callbacks.current.capture()) === acknowledged.current) return;
    timer.current = setTimeout(() => void write(callbacks.current.capture(), false), 650);
    return cancelTimer;
  }, [identity, signature, state.status]);

  const saveVersion = () => { cancelTimer(); return write(callbacks.current.capture(), true); };
  const restore = async (key: string) => {
    if (!owns() || paused.current) return null;
    cancelTimer();
    if (preserveCurrentOnRestore && content(callbacks.current.capture()) !== namedContent.current) {
      const preserved = await write(callbacks.current.capture(), true);
      if (!preserved) return null;
    }
    const generation = ++epoch.current;
    const inputVersion = callbacks.current.editVersion();
    update({ status: 'restoring', error: '' });
    await sequence.current.catch(() => null);
    if (!owns() || generation !== epoch.current) return null;
    try {
      const [sourceResponse, currentResponse] = await Promise.all([getQuoteEditorVersion(id, key), getQuoteEditorDraft(id)]);
      if (!owns() || generation !== epoch.current) return null;
      if (callbacks.current.editVersion() !== inputVersion) throw new Error('You edited the quote while the version was loading. Your current work is retained; choose the version again to restore it.');
      const source = sourceResponse?.data?.data;
      const latest = currentResponse?.data?.data;
      if (!source?.snapshot || !latest?.baseRevision || source.baseRevision !== latest.baseRevision || source.requestKey !== latest.requestKey) {
        throw new Error('The quote changed while this version was loading. Keep your current work and choose the version again.');
      }
      stateRef.current = latest;
      retryCommand.current = null;
      // Selecting a version is an explicit private restore, not recovery of a stale working copy.
      // Rebase its intended client values on the freshly checked canonical revision.
      const restoredSource = { ...source.snapshot,
        ...(source.snapshot._base ? { _base: latest.canonical } : {}),
        worksheetMembership: source.snapshot.worksheetMembership ? { ...source.snapshot.worksheetMembership, requestKey: latest.requestKey } : undefined,
        positions: source.snapshot.positions.map(row => source.snapshot.worksheetMembership?.requestKey !== latest.requestKey
          ? { ...row, clientRequestDraftDecisions: [] } : row) };
      const snapshot = callbacks.current.apply(restoredSource, latest.canonical, latest.requestKey);
      namedContent.current = content(snapshot);
      update({ selected: key, versions: latest.versions });
      return await write(snapshot, false, generation);
    } catch (error) {
      if (owns() && generation === epoch.current) update({ status: 'error', error: message(error) });
      return null;
    }
  };
  const retry = async () => {
    if (!owns()) return null;
    const generation = epoch.current;
    if (state.status === 'unavailable' || state.status === 'conflict') {
      try {
        const latest = (await getQuoteEditorDraft(id))?.data?.data;
        if (!owns() || generation !== epoch.current) return null;
        if (!latest?.baseRevision) throw new Error('The latest account draft could not be read. Keep your edits open and retry.');
        const local = { ...callbacks.current.capture(), _base: stateRef.current?.working?.snapshot ?? initialSnapshot.current };
        const snapshot = callbacks.current.apply(local, latest.working?.snapshot ?? latest.canonical, latest.requestKey);
        stateRef.current = latest;
        retryCommand.current = null;
        update({ versions: latest.versions });
        return await write(snapshot, false, generation);
      } catch (error) {
        if (owns() && generation === epoch.current) update({ error: message(error) });
        return null;
      }
    }
    return write(callbacks.current.capture(), retryCommand.current?.version ?? false);
  };
  const runOperational = async <T,>(operation: () => Promise<T>, reconcileDraft = false): Promise<T> => {
    if (!enabled) return operation();
    if (!owns()) throw new Error(sessionMessage);
    cancelTimer();
    ++epoch.current;
    paused.current = true;
    update({ status: 'paused' });
    await sequence.current.catch(() => null);
    const priorCanonical = stateRef.current?.canonical;
    try {
      if (!owns()) throw new Error(sessionMessage);
      return await operation();
    } finally {
      try {
        if (!owns()) throw new Error(sessionMessage);
        const latest = (await getQuoteEditorDraft(id))?.data?.data;
        if (owns()) {
          if (!latest?.baseRevision) throw new Error('The saved quote revision could not be read. Keep your edits open and retry saving.');
          if (reconcileDraft) {
            callbacks.current.apply({ ...callbacks.current.capture(), _base: priorCanonical }, latest.canonical, latest.requestKey);
          }
          stateRef.current = latest;
          retryCommand.current = null;
          update({ status: 'pending', versions: latest.versions, error: '' });
        }
      } catch (error) {
        if (owns()) update({ status: reconcileDraft ? 'conflict' : 'unavailable', error: message(error) });
        if (reconcileDraft) throw error;
      }
      finally { paused.current = false; }
    }
  };
  return { ...state, saveVersion, restore, retry, runOperational, ready: owns() && Boolean(stateRef.current),
    hasUnsaved: enabled && content(capture()) !== acknowledged.current,
    invalidate: async () => { cancelTimer(); ++epoch.current; await sequence.current.catch(() => null); if (owns()) stateRef.current = null; } };
}
